import { useList, useMountEffect } from "@react-hookz/web";
import type { MouseEventHandler } from "react";
import React, { useCallback, useEffect, useState } from "react";
import { getLoginDialogTitleAndDescription } from "scmp-sdk/components/acquisition-popup-container/display/helpers";
import { CloseButton } from "scmp-sdk/components/acquisition-popup-container/display/popups/common/close-button";
import { PopupBackdrop } from "scmp-sdk/components/acquisition-popup-container/display/styles";
import type { PopupProps } from "scmp-sdk/components/acquisition-popup-container/types";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { FollowAction, FollowType } from "scmp-sdk/components/follow-button/enums";
import { Modal } from "scmp-sdk/components/modal";

import { queryFromConfig } from "./actions";
import {
  Button,
  CheckboxContainer,
  ConfirmationContainer,
  Container,
  Description,
  Item,
  ItemLabel,
  ManageFollowAnchor,
  SelectionContainer,
  StyledIconAdd,
  StyledIconChecked,
  StyledIconConfirmation,
  ThankYouTitle,
  Title,
} from "./styles";
import type { TopicPackageFollow } from "./types";

export type Props = PopupProps<{
  confirmedConfig?: {
    checkedItemIndex?: number[];
  };
  topicPackageFollow: TopicPackageFollow & { key: string };
}>;

export const TopicPackageFollowPopup: React.FunctionComponent<Props> = ({
  confirmedConfig,
  modalContainerReference,
  onClose,
  topicPackageFollow,
}) => {
  const selectionConfig = topicPackageFollow.stepForm.step1;
  const confirmationConfig = topicPackageFollow.stepForm.step2;
  const { displayName, key, shouldShowThankYouPopup, trackingActionName } = topicPackageFollow;
  const { config, handleFollow, handleOpenLoginDialog, handleRoutePush, handleTrackGA4, user } =
    useSdkContext();

  const campaignName = trackingActionName;

  useMountEffect(() => {
    if (confirmedConfig) {
      if (shouldShowThankYouPopup) {
        handleTrackGA4({
          action: "imp",
          category: "promopopup",
          customized_parameters: {
            cta_type: "follow",
            internal_campaign: campaignName,
            step: "2",
          },
          subcategory: "",
        });
      }

      handleFollowConfirm();
      if (!shouldShowThankYouPopup) {
        onClose();
      }
      return;
    }

    handleTrackGA4({
      action: "imp",
      category: "promopopup",
      customized_parameters: {
        cta_type: "follow",
        internal_campaign: campaignName,
        step: "1",
      },
      subcategory: "",
    });
  });

  const handleCloseClick = () => {
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "follow",
        internal_campaign: campaignName,
        message: "close button",
        step: "1",
      },
      subcategory: "",
    });
    onClose();
  };

  const [itemList, itemListActions] = useList(
    selectionConfig.topicPackageDefaultOptionList.map((item, index) =>
      confirmedConfig?.checkedItemIndex
        ? { ...item, checked: confirmedConfig.checkedItemIndex.includes(index) }
        : { ...item, checked: item.defaultChecked },
    ),
  );
  useEffect(() => {
    itemListActions.reset();
  }, [
    itemListActions,
    selectionConfig.topicPackageDefaultOptionList,
    confirmedConfig?.checkedItemIndex,
  ]);
  const handleItemClick = (index: number) => {
    const item = itemList[index];
    itemListActions.updateAt(index, { ...item, checked: !item.checked });
    handleTrackGA4({
      action: "click",
      category: "follow",
      customized_parameters: {
        action_type: item.checked ? "unfollow" : "follow",
        follow_type: "topic_package",
        internal_campaign: campaignName,
        package_name: item.optionLabel,
        topic_id: item.topicIdList.join(", "),
        trigger_point: "ppto",
      },
      subcategory: "",
    });
  };

  const [hasConfirmed, setHasConfirmed] = useState(
    confirmedConfig?.checkedItemIndex && confirmedConfig.checkedItemIndex.length > 0,
  );

  const handleFollowConfirm = useCallback(() => {
    if (!user) {
      const checkedIndex = itemList.reduce<number[]>((list, item, index) => {
        if (item.checked) list.push(index);
        return list;
      }, []);
      const destination = new URL(globalThis.location.origin + globalThis.location.pathname);
      destination.search = queryFromConfig({ checkedIndex, configKey: key });

      const { description, title } = getLoginDialogTitleAndDescription({
        campaignName: displayName,
        type: "topics",
      });

      handleOpenLoginDialog({
        description,
        destination: destination.toString(),
        ga4CustomParameter: {
          internal_campaign: campaignName,
          trigger_point: "ppto",
        },
        title,
      });
      return;
    }

    const checkedItemList = itemList.filter(item => item.checked);
    handleFollow({
      action: FollowAction.Follow,
      callback: () => {
        const checkedLabel = checkedItemList.map(item => item.optionLabel).join(", ");
        handleTrackGA4({
          action: "sys",
          category: "follow",
          customized_parameters: {
            action_type: "follow",
            follow_type: "topic_package",
            internal_campaign: campaignName,
            package_name: checkedLabel,
            topic_id: checkedItemList.flatMap(item => item.topicIdList).join(", "),
            trigger_point: "ppto",
          },
          subcategory: "result",
        });
      },
      entityIds: checkedItemList.flatMap(item => item.topicIdList),
      followType: FollowType.Topic,
    });
    setHasConfirmed(true);
  }, [
    campaignName,
    displayName,
    handleFollow,
    handleOpenLoginDialog,
    handleTrackGA4,
    itemList,
    key,
    user,
  ]);

  const shouldDisableConfirmButton = itemList.every(item => !item.checked);
  const handleConfirmSelectionClick = () => {
    if (shouldDisableConfirmButton) return;
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "follow",
        internal_campaign: campaignName,
        message: selectionConfig.buttonText,
        step: "1",
      },
      subcategory: "",
    });

    handleFollowConfirm();
    if (!shouldShowThankYouPopup) {
      onClose();
    }
  };

  const handleConfirmContinueClick = () => {
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "follow",
        internal_campaign: campaignName,
        message: confirmationConfig.buttonText,
        step: "2",
      },
      subcategory: "",
    });
    onClose();
  };

  const handleManageFollowAnchorClick: MouseEventHandler = event => {
    event?.preventDefault();
    const manageFollowRelativeUrlPath = "/manage/following?module=topic_package&pgtype=article";
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "follow",
        destination_url: manageFollowRelativeUrlPath,
        internal_campaign: campaignName,
        message: confirmationConfig.bottomLinkText,
        step: "2",
      },
      subcategory: "",
    });
    const manageFollowUrl = `${config.accounts.host}${manageFollowRelativeUrlPath}`;
    void handleRoutePush(manageFollowUrl, { isExternal: true });
  };

  if (!shouldShowThankYouPopup && hasConfirmed) return null;

  return (
    <Modal
      BackdropComponent={PopupBackdrop}
      container={modalContainerReference?.current}
      fadeTimeout={400}
      open={true}
    >
      <Container>
        <CloseButton onClose={handleCloseClick} />
        {hasConfirmed ? (
          <ConfirmationContainer>
            <StyledIconConfirmation />
            <ThankYouTitle
              dangerouslySetInnerHTML={{
                __html: confirmationConfig.title,
              }}
            />
            <Description
              dangerouslySetInnerHTML={{
                __html: confirmationConfig.description,
              }}
            />
            <Button
              dangerouslySetInnerHTML={{
                __html: confirmationConfig.buttonText,
              }}
              onClick={handleConfirmContinueClick}
            />
            <ManageFollowAnchor
              dangerouslySetInnerHTML={{
                __html: confirmationConfig.bottomLinkText,
              }}
              onClick={handleManageFollowAnchorClick}
            />
          </ConfirmationContainer>
        ) : (
          <SelectionContainer>
            <Title
              dangerouslySetInnerHTML={{
                __html: selectionConfig.title,
              }}
            />
            <CheckboxContainer>
              {selectionConfig.topicPackageDefaultOptionList.map((item, index) => (
                <Item
                  $isChecked={itemList[index].checked}
                  key={index}
                  onClick={() => handleItemClick(index)}
                >
                  <StyledIconAdd />
                  <ItemLabel
                    dangerouslySetInnerHTML={{
                      __html: item.optionLabel,
                    }}
                  />
                  <StyledIconChecked />
                </Item>
              ))}
            </CheckboxContainer>
            <Description
              dangerouslySetInnerHTML={{
                __html: selectionConfig.description,
              }}
            />
            <Button
              $isDisabled={shouldDisableConfirmButton}
              dangerouslySetInnerHTML={{
                __html: selectionConfig.buttonText,
              }}
              onClick={handleConfirmSelectionClick}
            />
          </SelectionContainer>
        )}
      </Container>
    </Modal>
  );
};
